// Shutdown notice copy (Oct 31 2026). Shared by the site-wide <ShutdownNotice>
// modal and the standalone /shutdown page so both always say the same thing.

export const PRIVY_HOME_URL = "https://home.privy.io";
export const SHUTDOWN_DATE_LABEL = "October 31";

const link =
  "font-medium text-gray-900 dark:text-white underline underline-offset-4 decoration-gray-300 dark:decoration-gray-600 hover:decoration-gray-900 dark:hover:decoration-white";

const STEPS = [
  {
    title: "Go to Privy Home",
    body: (
      <>
        Open{" "}
        <a href={PRIVY_HOME_URL} target="_blank" rel="noopener noreferrer" className={link}>
          home.privy.io
        </a>
        . Privy is the wallet provider FrenFlow used. Your wallet lives with Privy, not with FrenFlow, so you
        can export it there without our app.
      </>
    ),
  },
  {
    title: "Sign in the same way you did on FrenFlow",
    body: (
      <>
        Use exactly the same login method: email, Google, X, Telegram or external wallet. A different method
        signs you into a different, empty account.
      </>
    ),
  },
  {
    title: "Find your FrenFlow wallets",
    body: (
      <>
        You will see the wallets created for you by FrenFlow: an <strong>Ethereum</strong> wallet (used on
        Polygon and BNB Chain) and, if you traded Kalshi markets, a <strong>Solana</strong> wallet.
      </>
    ),
  },
  {
    title: "Export the private key",
    body: (
      <>
        Select the wallet and choose <strong>Export</strong>. Privy will verify it is you and then show the
        private key in a secure window. Copy it and store it somewhere safe, such as a password manager.
      </>
    ),
  },
  {
    title: "Import it into a wallet you control",
    body: (
      <>
        Import the Ethereum key into <strong>Rabby</strong> or <strong>MetaMask</strong>, and the Solana key
        into <strong>Phantom</strong>. From then on, that wallet gives you full access to your funds with or
        without FrenFlow.
      </>
    ),
  },
];

const VENUES = [
  {
    name: "Polymarket",
    body: "Connect your imported Ethereum wallet on polymarket.com. Your positions and pUSD balance are on Polymarket and are linked to this wallet.",
  },
  {
    name: "Kalshi",
    body: "Kalshi positions bought through FrenFlow are tokens in your Solana wallet. Import the Solana key into Phantom to see and manage them.",
  },
  {
    name: "Predict.fun",
    body: "Connect your imported Ethereum wallet on predict.fun to see your BNB Chain positions.",
  },
];

const FAQ = [
  {
    q: "Is my money safe?",
    a: "FrenFlow was always self-custodial. We never held your funds. Everything sits in wallets and venue accounts that belong to you, and exporting your key gives you direct access to them.",
  },
  {
    q: "I logged in with MetaMask or another external wallet.",
    a: "Then there is nothing to export for that wallet. You already hold its key. Just connect it directly on Polymarket, Kalshi or Predict.fun.",
  },
  {
    q: `What stops working on ${SHUTDOWN_DATE_LABEL}?`,
    a: "frenflow.com, copy trading, alerts and the Telegram bot. Open positions are not closed and stay in your wallet.",
  },
  {
    q: "Will FrenFlow ever ask for my private key?",
    a: "No. Nobody from FrenFlow will ever ask for your key or seed phrase, by email, Telegram, X or any other channel. Anyone who does is a scammer.",
  },
];

export function ShutdownHeader({ titleId }: { titleId?: string }) {
  return (
    <header>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Service notice</p>
      <h1 id={titleId} className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.15]">
        FrenFlow is shutting down on {SHUTDOWN_DATE_LABEL}.
      </h1>
      <p className="mt-5 text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
        After that date frenflow.com and the Telegram bot will stop working. Your funds are not affected:
        FrenFlow is self-custodial, so your wallet and your positions belong to you. Before {SHUTDOWN_DATE_LABEL},
        please export your wallet&apos;s private key from Privy so you keep full access.
      </p>
      <a
        href={PRIVY_HOME_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-7 inline-flex items-center gap-2 rounded-lg bg-gray-900 dark:bg-white px-5 py-3 text-sm font-medium text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
      >
        Export my wallet on Privy
        <span aria-hidden>↗</span>
      </a>
    </header>
  );
}

export function ShutdownBody() {
  return (
    <>
      <section className="mt-14">
        <h2 className="text-xl font-semibold tracking-tight">How to export your wallet</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Takes about two minutes. Done entirely on Privy.</p>
        <ol className="mt-8 space-y-7">
          {STEPS.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-[#18181D] text-sm font-medium text-gray-700 dark:text-gray-300">
                {i + 1}
              </span>
              <div>
                <h3 className="font-medium">{step.title}</h3>
                <p className="mt-1.5 leading-relaxed text-gray-600 dark:text-gray-400">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-12 rounded-xl bg-gray-50 dark:bg-[#18181D] p-6">
        <h2 className="font-semibold">Keep your key private</h2>
        <p className="mt-2 leading-relaxed text-gray-600 dark:text-gray-400">
          Your private key is full control of your funds. Never paste it into a website, never send it to
          anyone, and ignore any message offering &quot;help&quot; with the migration. FrenFlow will never ask
          for it.
        </p>
      </section>

      <section className="mt-14">
        <h2 className="text-xl font-semibold tracking-tight">After exporting: where your positions are</h2>
        <div className="mt-7 space-y-6">
          {VENUES.map((v) => (
            <div key={v.name}>
              <h3 className="font-medium">{v.name}</h3>
              <p className="mt-1.5 leading-relaxed text-gray-600 dark:text-gray-400">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14">
        <h2 className="text-xl font-semibold tracking-tight">Questions</h2>
        <div className="mt-7 space-y-7">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium">{f.q}</h3>
              <p className="mt-1.5 leading-relaxed text-gray-600 dark:text-gray-400">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="mt-16 pt-8 border-t border-gray-200 dark:border-[#1A1A1A] text-sm text-gray-500 dark:text-gray-400">
        <p>Thank you for trading with us.</p>
        <p className="mt-2">— The FrenFlow team</p>
      </footer>
    </>
  );
}
